<template>
	<div class="flex w-full flex-col">
		<DefaultHeading v-if="showHeader" class="m-auto mb-8">
			<slot name="heading">Similar vehicles</slot>
		</DefaultHeading>

		<p v-else class="m-5 text-center text-xl font-medium text-gray-650">
			<slot name="heading">Similar vehicles</slot>
		</p>

		<div v-if="!showAll">
			<div
				v-if="!isLoading"
				class="similar-vehicles relative m-auto"
				:class="[carouselWidth, isStacked ? 'is-stacked' : '']"
			>
				<Carousel
					ref="myCarousel"
					class="featured-vehicles"
					:items-to-show="1.2"
					:transition="500"
					:breakpoints="breakpoints"
					:wrap-around="wrapAround"
					:snap-align="'center'"
					@init="updateCarousel"
				>
					<Slide
						v-for="sliderProduct in sliderProducts"
						:key="sliderProduct.id"
						:class="[isStacked ? 'featured-slide' : '']"
					>
						<div
							class="h-full w-full justify-center"
							:class="[
								!isStacked
									? `px-2 md:pt-2 md:${itemsGapX}`
									: itemsGapX,
							]"
						>
							<FilterCarCardSubscription
								v-if="carType === 'subscription'"
								class="featured-vehicles-inner"
								:product="sliderProduct"
								:show-slide-arrows="showSlideArrows"
							/>
							<FilterCarCardBuyUsed
								v-if="carType === 'used'"
								class="featured-vehicles-inner"
								:product="sliderProduct"
								:show-slide-arrows="showSlideArrows"
							/>
						</div>
					</Slide>

					<Slide
						v-if="showMore"
						:class="[isStacked ? 'featured-slide' : '']"
					>
						<div
							class="h-full w-full justify-center"
							:class="[
								!isStacked
									? `px-2 md:pt-2 md:${itemsGapX}`
									: itemsGapX,
							]"
						>
							<CarbarLink
								:href="showMoreUrl"
								target="_blank"
								class="flex h-full w-full"
							>
								<div
									class="flex h-full w-full flex-wrap items-center justify-center bg-gray-120"
								>
									<span
										class="text-gray-800 underline decoration-gray-800"
									>
										Show me more
									</span>
									<div
										class="absolute bottom-0 left-0 right-0 top-0 flex items-end justify-end px-10"
									>
										<div
											class="mx-auto font-heading text-[80px] font-bold leading-tight text-gray-400 opacity-20"
										>
											more
										</div>
									</div>
								</div>
							</CarbarLink>
						</div>
					</Slide>
					<template #addons>
						<ClientOnly>
							<div
								class="similar-vehicles-nav flex items-center justify-center"
							>
								<Navigation :button="true" />
							</div>
						</ClientOnly>
					</template>
				</Carousel>
			</div>
			<div
				v-if="isLoading"
				:class="[
					isStacked
						? 'relative m-auto w-full md:w-2/3'
						: 'flex flex-row space-x-6',
				]"
			>
				<ContentLoader
					v-for="sliderProduct in isStacked ? 1 : carouselItemsToShow"
					:key="sliderProduct.id"
					class="relative m-auto"
					width="w-full"
					height="h-[400px]"
				/>
			</div>
		</div>

		<div v-else>
			<div
				v-if="!isLoading"
				class="grid grid-cols-1 gap-6 md:grid-cols-3"
			>
				<div
					v-for="sliderProduct in sliderProducts"
					:key="sliderProduct.id"
					class="similar-vehicles relative m-auto h-full w-full"
				>
					<FilterCarCardSubscription
						v-if="carType === 'subscription'"
						class="featured-vehicles-inner"
						:product="sliderProduct"
						:show-slide-arrows="showSlideArrows"
					/>
					<FilterCarCardBuyUsed
						v-if="carType === 'used'"
						class="featured-vehicles-inner"
						:product="sliderProduct"
						:show-slide-arrows="showSlideArrows"
					/>
				</div>
			</div>
			<div v-if="isLoading" class="grid grid-cols-1 gap-6 md:grid-cols-3">
				<ContentLoader
					v-for="sliderProduct in limit"
					:key="sliderProduct.id"
					class="relative m-auto"
					width="w-full"
					height="h-[400px]"
				/>
			</div>
		</div>
	</div>
</template>

<script setup>
import ContentLoader from '@/components/ContentLoader.vue'
import DefaultHeading from '@/components/headings/DefaultHeading.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import FilterCarCardBuyUsed from '@/components/product-catalog/cards/FilterCarCardBuyUsed.vue'
import FilterCarCardSubscription from '@/components/product-catalog/cards/FilterCarCardSubscription.vue'
import Vehicle from '@/models/Vehicle'
import { useProductCatalog } from '@/stores/product-catalog'
import qs from 'qs'

const productCatalog = useProductCatalog()

const props = defineProps({
	filters: {
		type: Object,
		default: () => ({}),
		required: false,
	},
	limit: {
		type: Number,
		default: 3,
		required: false,
	},
	product: {
		type: Object,
		default: () => null,
		required: false,
	},
	showHeader: {
		type: Boolean,
		default: true,
		required: false,
	},
	itemsGapX: {
		type: String,
		default: 'px-3',
		required: false,
	},
	carouselWidth: {
		type: String,
		default: 'w-full',
		required: false,
	},
	wrapAround: {
		type: Boolean,
		default: false,
		required: false,
	},
	breakpoints: {
		type: Object,
		default: () => ({
			// 1px and up
			1: {
				itemsToShow: 1.1,
				snapAlign: 'center',
			},
			// 700px and up
			700: {
				itemsToShow: 2,
				snapAlign: 'center',
			},
			// 1024px and up
			1024: {
				itemsToShow: 3,
				snapAlign: 'center',
			},
		}),
		required: false,
	},
	itemsToShow: {
		type: Number,
		default: 1,
		required: false,
	},
	isStacked: {
		type: Boolean,
		default: false,
		required: false,
	},
	showSlideArrows: {
		type: Boolean,
		default: true,
		required: false,
	},
	showMoreThresholdLimit: {
		type: Number,
		default: 20,
		required: false,
	},
	carType: {
		type: String,
		default: 'subscription',
		required: false,
	},
	showAll: {
		type: Boolean,
		default: false,
		required: false,
	},
})

const emit = defineEmits(['loaded'])

const products = ref([])
const foundSimilar = ref(false)
const showMore = ref(false)
const isLoading = ref(true)
const myCarousel = ref(null)
const windowWidth = ref()

const updateWindowWidth = () => {
	if (window !== undefined) {
		windowWidth.value = window.innerWidth
	}
}

const updateCarousel = () => myCarousel.value.restartCarousel()

const carouselItemsToShow = computed(() => {
	if (windowWidth.value >= 1024) {
		return 3
	}
	if (windowWidth.value >= 700) {
		return 2
	}
	return 1
})

const sliderProducts = computed(() => {
	const displayData = products.value

	return displayData
})

const vehicleQuery = computed(() => {
	if (props.product && props.product.stock_id) {
		return `v2/vehicle-similar/${props.product.stock_id}`
	}
	if (props.carType === 'subscription') {
		return 'v2/vehicles'
	}
	return 'v2/vehicles/used'
})

const showMoreUrl = computed(() => {
	let url =
		props.carType === 'subscription' ? '/cars/subscribe' : '/cars/buy/used'

	if (products.value.length === 0 || foundSimilar.value === false) {
		return url
	}

	if (props.carType !== 'subscription') {
		throw new Error(
			'Show More Url is not configured for used cars - price facets are for subscription'
		)
	}

	const query = Vehicle.custom(vehicleQuery.value).cancelKey(
		'show-more-vehicles'
	)

	const availablePriceValues = productCatalog.facets.priceFrom
		.filter((item) => item.value !== 'all')
		.map((item) => parseInt(item.value, 10))

	// Price story - From { PRICE OF CAR SELECTED - $50.00}, TO {PRICE OF CAR SELECTED + $50.00}
	let priceFrom = props.product?.pricing?.price
		? parseInt(props.product.pricing.price, 10) - 50
		: availablePriceValues[0]
	let priceTo = props.product?.pricing?.price
		? parseInt(props.product.pricing.price, 10) + 50
		: availablePriceValues[availablePriceValues.length - 1]

	priceFrom =
		priceFrom < availablePriceValues[0]
			? availablePriceValues[0]
			: priceFrom

	priceTo =
		priceTo > availablePriceValues[availablePriceValues.length - 1]
			? availablePriceValues[availablePriceValues.length - 1]
			: priceTo

	for (const price of availablePriceValues) {
		if (price < parseInt(priceFrom, 10)) {
			priceFrom = price
			break
		}
	}

	for (const price of availablePriceValues) {
		if (price > parseInt(priceTo, 10)) {
			priceTo = price
			break
		}
	}

	if (
		props.product?.pricing?.price &&
		priceFrom &&
		availablePriceValues.includes(priceFrom)
	) {
		query.where('priceFrom', priceFrom)
	}

	if (
		props.product?.pricing?.price &&
		priceTo &&
		priceFrom !== priceTo &&
		availablePriceValues.includes(priceTo)
	) {
		query.where('priceTo', priceTo)
	}

	if (
		props.product &&
		props.product.location &&
		props.product.location.split(',').length === 1
	) {
		url = `${url}/${props.product.location}`.toLowerCase()
	}

	if (props.product && props.product.body_type) {
		url = `${url}/${props.product.body_type}`.toLowerCase()
	}

	query.orderBy('price')

	const urlQuery = query._builder.query()

	const nestedQuery = qs.parse(urlQuery.replace(/^\?/, ''))

	const flattenObject = {
		priceTo: nestedQuery.filter?.priceTo,
		priceFrom: nestedQuery.filter?.priceFrom,
		sortBy: nestedQuery.sort,
	}

	const result = `?${qs.stringify(flattenObject, { encode: false })}`

	return `${url}${result}`
})

onMounted(async () => {
	if (!props.showAll && !props.isStacked) {
		updateWindowWidth()
	}

	const query = Vehicle.custom(vehicleQuery.value)
		.cancelKey('similar-vehicles')
		.limit(props.limit)

	if (props.filters) {
		for (const [filterKey, filterValue] of Object.entries(props.filters)) {
			if (Array.isArray(filterValue)) {
				query.whereIn(filterKey, filterValue)
			} else {
				query.where(filterKey, filterValue)
			}
		}
	}
	query.params({ 'exclude-facets': true })

	try {
		const { items, meta, is_similar } = await query.get()

		const responseProducts = items || []
		const responseMeta = meta || {}

		emit('loaded', responseProducts)

		products.value = responseProducts

		foundSimilar.value = !!is_similar

		showMore.value = responseMeta.total > props.showMoreThresholdLimit

		setTimeout(() => {
			isLoading.value = false
		}, 1000)
	} catch (err) {
		if (err.name === 'AbortError') return
	}
})
</script>
